// Choix de l’organisation (espace de travail) parmi les appartenances du compte, acceptation
// des invitations en attente.
import { Bandeau, Bouton, EtatVide, PuceEtat, Squelette } from '@eec/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RetourPage } from '../../commun/RetourPage';
import { requete } from '../../api/client';
import { ErreurApi, messageErreur } from '../../api/erreurs';
import type { Appartenance } from '../../api/types';
import { AffichageErreur } from '../../commun/EtatRequete';
import { CLE_CONTEXTE, useSession } from '../../session/FournisseurSession';
import { nomCompte } from '../../session/permissions';
import styles from './acces.module.css';
import { LogoComplet } from './Logo';

function libelleAppartenance(a: Appartenance): string {
  return a.organisation?.nom ?? a.institution ?? 'Espace institutionnel';
}

export function PageChoixContexte() {
  const { contexte, chargementContexte, erreurContexte, activerContexte, deconnecter, recharger } =
    useSession();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Choisir un espace de travail - Eau Énergie Connect';
  }, []);

  const activer = async (a: Appartenance) => {
    setErreur(null);
    setEnCours(a.id);
    try {
      await activerContexte(a.id);
      // Le super administrateur de la plateforme n'a pas de tableau de bord : il arrive sur les modules (D-080).
      navigate(a.institution === 'plateforme' ? '/plateforme/modules' : '/', { replace: true });
    } catch (e) {
      if (e instanceof ErreurApi && e.code === 'mfa_requis') {
        navigate('/mfa/activer', { state: { appartenanceId: a.id } });
        return;
      }
      setErreur(messageErreur(e));
    } finally {
      setEnCours(null);
    }
  };

  const accepter = async (a: Appartenance) => {
    setErreur(null);
    setEnCours(a.id);
    try {
      await requete(`/appartenances/${a.id}/accepter`, { methode: 'POST' });
      await client.invalidateQueries({ queryKey: CLE_CONTEXTE });
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(null);
    }
  };

  const appartenances = contexte?.appartenances ?? [];
  const activeId = contexte?.appartenance_active?.id;

  return (
    <div className={styles.page}>
      <LogoComplet />
      <main className={`${styles.carte} ${styles.large}`} id="contenu-principal">
        <div>
          <p className="cc-etiquette">{nomCompte(contexte) || 'Compte'}</p>
          <h1>Choisir un espace de travail</h1>
          {contexte?.appartenance_active ? (
            <RetourPage />
          ) : (
            <RetourPage vers="/citoyen" libelle="Retour au portail public" />
          )}
        </div>
        <p className={styles.aide}>
          Chaque organisation est un espace séparé : en changer recharge vos droits et efface de
          l&apos;écran les données de l&apos;espace précédent.
        </p>
        {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
        {chargementContexte ? (
          <Squelette lignes={3} />
        ) : erreurContexte ? (
          <AffichageErreur erreur={erreurContexte} onReessayer={() => void recharger()} />
        ) : appartenances.length === 0 ? (
          <EtatVide titre="Aucun espace de travail" icone={Building2}>
            <p>
              Votre compte n&apos;appartient encore à aucune organisation. L&apos;administrateur
              local doit vous inviter.
            </p>
          </EtatVide>
        ) : (
          <ul className={styles.choix} aria-label="Espaces de travail">
            {appartenances.map((a) => (
              <li key={a.id}>
                {a.statut === 'invitee' ? (
                  <div className={styles.choixBouton}>
                    <span>
                      <strong>{libelleAppartenance(a)}</strong>
                      <small>Invitation en attente de votre acceptation</small>
                    </span>
                    <Bouton petit chargement={enCours === a.id} onClick={() => void accepter(a)}>
                      Accepter l&apos;invitation
                    </Bouton>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.choixBouton}
                    disabled={a.statut !== 'active' || enCours !== null}
                    aria-current={a.id === activeId ? 'true' : undefined}
                    onClick={() => void activer(a)}
                  >
                    <span>
                      <strong>{libelleAppartenance(a)}</strong>
                      <small>
                        {a.roles
                          .filter((r) => r.statut === 'active')
                          .map((r) => r.libelle)
                          .join(', ') || 'Aucun rôle actif'}
                      </small>
                    </span>
                    {enCours === a.id ? (
                      <PuceEtat ton="attente" libelle="Ouverture en cours" />
                    ) : a.id === activeId ? (
                      <PuceEtat ton="ok" libelle="Espace actuel" />
                    ) : a.statut !== 'active' ? (
                      <PuceEtat
                        ton="neutre"
                        libelle={a.statut === 'suspendue' ? 'Suspendue' : 'Inactive'}
                      />
                    ) : null}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <Bouton
          variante="discret"
          onClick={() => void deconnecter().then(() => navigate('/connexion', { replace: true }))}
        >
          Se déconnecter
        </Bouton>
      </main>
    </div>
  );
}
